import { MAX_PAGE_SIZE } from '@vv/common';

import type {
  ApiResponse,
  Article,
  ArticleDetail,
  Fair,
  FairSibling,
  HealthCheckData,
  JobOffer,
  JobOfferDetail,
  MediaItem,
  OfferFilters,
  PaginatedData,
  Pavilion,
  Stand,
  StandProfile,
  TreasureHunt,
} from '../types/api';

import { readFairDomain } from './fairDomain';

/**
 * The one place the API base URL is read. A trailing slash would double the
 * separator once paths are appended, so it is stripped here rather than at each
 * call site.
 */
function resolveBaseUrl(): string {
  const raw = import.meta.env.VITE_API_URL;

  if (raw === undefined || raw.trim() === '') {
    throw new Error('VITE_API_URL is not set. Copy .env.example to .env and set it.');
  }

  return raw.trim().replace(/\/+$/, '');
}

const API_BASE_URL = resolveBaseUrl();

type QueryValue = string | number | boolean | readonly (string | number)[] | undefined;

/**
 * Builds the query string, dropping empty values so an unset filter does not
 * appear as `?q=`. Array values are sent comma-joined, which is the form the
 * API's query DTOs parse.
 */
function buildQuery(params: Record<string, QueryValue> = {}): string {
  const search = new URLSearchParams();

  // Deployed, the API resolves the fair from the domain the visitor used, which
  // CloudFront passes on. `?domain=` overrides that where the domain names no
  // fair: local development and the dev stage's cloudfront.net domain. Read per
  // call rather than hoisted to a module constant: the testing override is
  // written to storage during startup, and a constant would capture the value
  // from before that ran.
  const fairDomain = readFairDomain();

  if (fairDomain !== undefined && fairDomain !== '') {
    search.set('domain', fairDomain);
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        search.set(key, value.join(','));
      }

      continue;
    }

    const asString = String(value);

    if (asString !== '') {
      search.set(key, asString);
    }
  }

  const query = search.toString();

  return query === '' ? '' : `?${query}`;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status: number,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        // Only when there is a body to describe. `Content-Type: application/json`
        // is not a CORS-safelisted value, so sending it on a plain GET triggers a
        // preflight — and API Gateway forwards OPTIONS to Nest, which has no such
        // route and answers 404, failing the check and blocking the real request.
        ...(init?.body !== undefined && { 'Content-Type': 'application/json' }),
        ...init?.headers,
      },
    });
  } catch (cause) {
    // fetch rejects only on network/CORS failure; there is no envelope to read.
    throw new ApiError(
      cause instanceof Error ? cause.message : 'Network request failed',
      'NETWORK_ERROR',
      0,
    );
  }

  let body: ApiResponse<T>;

  try {
    body = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError('Response was not valid JSON', 'INVALID_RESPONSE', response.status);
  }

  if (!body.success) {
    throw new ApiError(body.error.message, body.error.code, response.status, body.correlationId);
  }

  return body.data;
}

// `| undefined` on every field, not just `?`: the workspace sets
// `exactOptionalPropertyTypes`, so a caller passing `q: undefined` for an unset
// filter would otherwise not typecheck. `buildQuery` drops undefined values.
export interface StandSearchParams {
  page?: number | undefined;
  limit?: number | undefined;
  q?: string | undefined;
}

export interface OfferSearchParams extends StandSearchParams {
  pavilionIds?: readonly number[] | undefined;
  categoryIds?: readonly number[] | undefined;
  standIds?: readonly number[] | undefined;
  locations?: readonly string[] | undefined;
}

export const apiClient = {
  getHealth: (): Promise<HealthCheckData> => request<HealthCheckData>('/health'),

  echo: (message: string, times: number): Promise<{ echoed: string[] }> =>
    request<{ echoed: string[] }>('/health/echo', {
      method: 'POST',
      body: JSON.stringify({ message, times }),
    }),

  /** The fair serving this domain. Every other endpoint is scoped to it. */
  getFair: (): Promise<Fair> => request<Fair>(`/fair${buildQuery()}`),

  getFairSiblings: (): Promise<FairSibling[]> =>
    request<FairSibling[]>(`/fair/siblings${buildQuery()}`),

  /** Null where this fair does not run "Lov na blago". */
  getTreasureHunt: (): Promise<TreasureHunt | null> =>
    request<TreasureHunt | null>(`/treasure-hunt${buildQuery()}`),

  getPavilions: (): Promise<Pavilion[]> => request<Pavilion[]>(`/pavilions${buildQuery()}`),

  getPavilion: (slug: string): Promise<Pavilion> =>
    request<Pavilion>(`/pavilions/${encodeURIComponent(slug)}${buildQuery()}`),

  /** Not paginated: a pavilion's exhibitor list is returned whole. */
  getPavilionStands: (slug: string): Promise<Stand[]> =>
    request<Stand[]>(`/pavilions/${encodeURIComponent(slug)}/stands${buildQuery()}`),

  getStands: (params: StandSearchParams = {}): Promise<PaginatedData<Stand>> =>
    request<PaginatedData<Stand>>(`/stands${buildQuery({ ...params })}`),

  getStand: (slug: string): Promise<Stand> =>
    request<Stand>(`/stands/${encodeURIComponent(slug)}${buildQuery()}`),

  /** Not paginated: one exhibitor's offers are returned whole. */
  getStandOffers: (slug: string): Promise<JobOffer[]> =>
    request<JobOffer[]>(`/stands/${encodeURIComponent(slug)}/offers${buildQuery()}`),

  getStandGallery: (slug: string): Promise<MediaItem[]> =>
    request<MediaItem[]>(`/stands/${encodeURIComponent(slug)}/gallery${buildQuery()}`),

  getStandProfile: (slug: string): Promise<StandProfile> =>
    request<StandProfile>(`/stands/${encodeURIComponent(slug)}/profile${buildQuery()}`),

  getOffers: (params: OfferSearchParams = {}): Promise<PaginatedData<JobOffer>> =>
    request<PaginatedData<JobOffer>>(`/offers${buildQuery({ ...params })}`),

  /** Every page of a search, for listings that group offers rather than page them. */
  getAllOffers: async (
    params: Omit<OfferSearchParams, 'page' | 'limit'> = {},
  ): Promise<JobOffer[]> => {
    const items: JobOffer[] = [];

    for (let page = 1; ; page += 1) {
      const result = await apiClient.getOffers({ ...params, page, limit: MAX_PAGE_SIZE });

      items.push(...result.items);

      if (page >= result.pagination.totalPages) {
        return items;
      }
    }
  },

  getOfferFilters: (): Promise<OfferFilters> =>
    request<OfferFilters>(`/offers/filters${buildQuery()}`),

  getOffer: (id: number): Promise<JobOfferDetail> =>
    request<JobOfferDetail>(`/offers/${id}${buildQuery()}`),

  /** Not paginated: the article list is returned whole. */
  getArticles: (): Promise<Article[]> => request<Article[]>(`/articles${buildQuery()}`),

  getArticle: (id: number): Promise<ArticleDetail> =>
    request<ArticleDetail>(`/articles/${id}${buildQuery()}`),
};
